import { useState } from 'react'
import { api, ApiError, type CreatorLanguage, type CreatorPool, type CreatorPoolInput, type PoolPlatform } from '@/api/client'
import { LANGUAGES, useCreatorMutation } from '@/lib/creators'
import { useTargets } from '@/lib/queries'
import { Button, Drawer, Field, FieldGroup, Input, PlatformMark, Segmented, Select, Textarea, errorText } from '@/ui'

/** Comma or line separated words, empty ones dropped. */
export const words = (v: string) =>
  v
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)

const whole = (v: string) => (v.trim() !== '' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN)

export interface PoolDraft {
  name: string
  keywords: string
  platform: PoolPlatform
  fMin: string
  fMax: string
  activeDays: string
  brands: boolean
  language: CreatorLanguage
  seeds: string
  search: boolean
  auto: boolean
  lookups: string
  igId: string
}

/** The rules the server applies on save (creators pool validation), so a
 *  mistake shows before the request. The server's own message still wins
 *  for anything this misses. */
export function validatePool(d: PoolDraft): string | null {
  if (!d.name.trim()) return '이름을 적는다.'
  const lo = whole(d.fMin)
  if (Number.isNaN(lo)) return '팔로워 최소는 0 이상의 숫자다.'
  if (d.fMax.trim() !== '') {
    const hi = whole(d.fMax)
    if (Number.isNaN(hi)) return '팔로워 최대는 숫자이거나 비워 둔다.'
    if (hi < lo) return '팔로워 최대는 최소보다 작을 수 없다.'
  }
  const days = whole(d.activeDays)
  if (Number.isNaN(days) || days > 365) return '최근 활동은 0~365일이다. 0이면 보지 않는다.'
  const n = whole(d.lookups)
  if (Number.isNaN(n) || n < 10 || n > 500) return '하루 조회 수는 10~500이다.'
  if (words(d.seeds).length > 50) return '씨앗 계정은 50개까지다.'
  return null
}

/** The pool form, in a drawer: 새 풀 when `pool` is missing, the criteria
 *  card's 편집 otherwise. Mount it only while open. */
export function PoolForm({
  pool,
  onClose,
  onSaved,
}: {
  pool?: CreatorPool
  onClose: () => void
  onSaved: (p: CreatorPool) => void
}) {
  const targets = useTargets().data ?? []
  const save = useCreatorMutation((v: CreatorPoolInput) => (pool ? api.updateCreatorPool(pool.id, v) : api.createCreatorPool(v)))
  const [d, setD] = useState<PoolDraft>({
    name: pool?.name ?? '',
    keywords: (pool?.keywords ?? []).join(', '),
    platform: pool?.platform ?? 'instagram',
    fMin: String(pool?.followers_min ?? 500),
    fMax: pool?.followers_max != null ? String(pool.followers_max) : '',
    activeDays: String(pool?.active_days ?? 30),
    brands: pool?.exclude_brands ?? true,
    language: pool?.language ?? 'en',
    seeds: (pool?.seed_handles ?? []).join('\n'),
    search: pool?.search_enabled ?? true,
    auto: pool?.discovery_enabled ?? false,
    lookups: String(pool?.daily_lookups ?? 100),
    igId: String(pool?.instagram_target_id ?? 0),
  })
  const [problem, setProblem] = useState<string | null>(null)
  const set = <K extends keyof PoolDraft>(k: K, v: PoolDraft[K]) => setD((cur) => ({ ...cur, [k]: v }))

  const submit = async () => {
    const found = validatePool(d)
    setProblem(found)
    if (found) return
    const body: CreatorPoolInput = {
      name: d.name.trim(),
      keywords: words(d.keywords),
      platform: d.platform,
      followers_min: whole(d.fMin),
      followers_max: d.fMax.trim() === '' ? null : whole(d.fMax),
      active_days: whole(d.activeDays),
      exclude_brands: d.brands,
      language: d.language,
      seed_handles: words(d.seeds).map((s) => s.replace(/^@/, '')),
      search_enabled: d.search,
      discovery_enabled: d.auto,
      daily_lookups: whole(d.lookups),
      instagram_target_id: d.platform === 'instagram' ? Number(d.igId) || null : null,
    }
    try {
      onSaved(await save.mutateAsync(body))
    } catch {
      /* the server's message shows below from save.error */
    }
  }

  const accounts = targets.filter((t) => t.platform === 'instagram' && t.status === 'active')
  const serverError = save.error as unknown
  const serverText = serverError ? (serverError instanceof ApiError ? serverError.message : errorText(serverError)) : null

  return (
    <Drawer
      open
      onClose={onClose}
      title={pool ? '풀 조건 편집' : '새 풀'}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
            {pool ? '저장' : '만들기'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FieldGroup title="누구를 찾나" description="이 조건에 맞는 사람만 풀에 들어온다. 나중에 고칠 수 있다.">
          <Field label="풀 이름" required>
            <Input value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="예: 뷰티 인스타그램" />
          </Field>
          <Field label="플랫폼">
            <Segmented
              label="플랫폼"
              value={d.platform}
              onChange={(v) => set('platform', v)}
              options={[
                { value: 'instagram', label: 'Instagram' },
                { value: 'bluesky', label: 'Bluesky' },
              ]}
            />
            <span className="mt-1.5 flex items-center gap-1.5 text-meta text-muted-foreground">
              <PlatformMark platform={d.platform} />한 풀에는 한 플랫폼만 담는다.
            </span>
          </Field>
          <Field label="분야 키워드" hint="쉼표나 줄바꿈으로 나눈다. 예: 뷰티, 메이크업, 스킨케어">
            <Textarea rows={2} value={d.keywords} onChange={(e) => set('keywords', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="팔로워 최소">
              <Input inputMode="numeric" value={d.fMin} onChange={(e) => set('fMin', e.target.value)} />
            </Field>
            <Field label="팔로워 최대" hint="비우면 상한이 없다.">
              <Input inputMode="numeric" value={d.fMax} onChange={(e) => set('fMax', e.target.value)} />
            </Field>
          </div>
          <Field label="최근 활동 (일)" hint="이 기간 안에 글을 올린 사람만. 0이면 보지 않는다.">
            <Input inputMode="numeric" value={d.activeDays} onChange={(e) => set('activeDays', e.target.value)} />
          </Field>
          <label className="inline-flex items-center gap-2 text-body">
            <input type="checkbox" checked={d.brands} onChange={(e) => set('brands', e.target.checked)} />
            브랜드 계정 제외
          </label>
          <Field label="언어" hint="검색어와 주제 판별에 쓴다.">
            <Select value={d.language} onChange={(e) => set('language', e.target.value as CreatorLanguage)}>
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
          </Field>
        </FieldGroup>

        <FieldGroup
          title="어디서 찾나"
          description="씨앗 계정과 검색에서 시작해, 찾은 사람의 글에 언급된 계정을 따라가며 넓힌다. 조건을 통과한 사람만 넣는다."
        >
          <Field label="씨앗 계정 (선택)" hint="한 줄에 하나, 50개까지.">
            <Textarea rows={3} value={d.seeds} onChange={(e) => set('seeds', e.target.value)} className="font-mono text-meta" />
          </Field>
          <label className="inline-flex items-center gap-2 text-body">
            <input type="checkbox" checked={d.search} onChange={(e) => set('search', e.target.checked)} />
            검색 사용
          </label>
          <label className="inline-flex items-center gap-2 text-body">
            <input type="checkbox" checked={d.auto} onChange={(e) => set('auto', e.target.checked)} />
            매일 자동으로 찾기
          </label>
          <Field label="하루 조회 수" hint="10~500. 프로필 하나를 불러올 때마다 하나씩 쓴다.">
            <Input inputMode="numeric" value={d.lookups} onChange={(e) => set('lookups', e.target.value)} />
          </Field>
          {d.platform === 'instagram' && (
            <Field label="Instagram 조회 계정" hint="다른 사람의 공개 프로필을 불러올 때만 쓴다. 계정 메뉴에서 Instagram 계정을 먼저 만든다.">
              <Select value={d.igId} onChange={(e) => set('igId', e.target.value)}>
                <option value="0">— 지정 안 함 —</option>
                {accounts.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </FieldGroup>

        {problem && (
          <p role="alert" className="text-table text-status-failed">
            {problem}
          </p>
        )}
        {serverText && (
          <p role="alert" className="text-table text-status-failed">
            {serverText}
          </p>
        )}
      </div>
    </Drawer>
  )
}
